/**
 * useLocalStageServe — REUSE-STAGE0 Phase 1's serving layer. Wires
 * `localStageServeEngine.ts`'s ADOPTED-mode engine to a real `Peer` and
 * `useCommunalChat.ts`'s resident stage-0 worker (`ResidentStageZero`,
 * via its `residentStageZeroRef`), so a capable peer can serve its
 * already-loaded, already-embeddings-including `[0, driverLayers)` stage
 * to thin/text-relay clients — no second GPU load, no second download.
 *
 * ── Gating ────────────────────────────────────────────────────────────
 *
 * Active only when ALL of:
 *   1. `enabled` (the "Serve the first stage" opt-in — see
 *      `useHostingConsent.ts`'s `serveFirstStage`) is true.
 *   2. This tab holds a DEDICATED cross-tab leader lock
 *      (`leaderLockName`) — same `acquireLeaderLock` idiom
 *      `useCommunalChat.ts`/`useStagePipeline.ts` use, but a DIFFERENT
 *      lock name from the chat driver's own. Reusing the CHAT lock would
 *      be wrong: that lock is held only transiently, for the duration of
 *      one chat turn (`useCommunalChat.ts`'s `start()`/`finally`) — a
 *      persistent hold here (for as long as `enabled` is true) would
 *      starve this SAME tab's own `start()` from ever acquiring it. This
 *      dedicated lock instead answers "which ONE tab in this browser
 *      profile serves stage-0" — orthogonal to which tab (if any) is
 *      currently mid-chat-turn.
 *   3. `residentStageZeroRef.current` is non-null (the resident worker
 *      has actually loaded — see `useCommunalChat.ts`'s `serveFirstStage`
 *      doc comment: loading is lazy, triggered by the driver's own first
 *      chat turn after the toggle goes on).
 *
 * The resident ref is a plain mutable ref (not React state — a future
 * serving layer must never force `useCommunalChat` to re-render just to
 * expose a worker it already loaded), so this hook POLLS it
 * (`RESIDENT_POLL_MS`) rather than subscribing to a change event that
 * doesn't exist. A serving engine typically appears within one poll tick
 * after the driver's first post-toggle chat turn finishes loading it —
 * acceptable staleness for a UI-timescale feature (nothing here is
 * latency-sensitive at sub-second granularity).
 *
 * ── Cross-talk with `useStageHost.ts` ────────────────────────────────
 *
 * A peer can run BOTH this hook (serving `[0, driverLayers)`) AND
 * `useCommunalHost.ts`'s own `useStageHost` (serving
 * `[driverLayers, totalLayers)`) at once — both listen on the SAME
 * `peer.onTool`/`peer.onStageFrame`. This hook only ever acts on a
 * `stage.session.open`/`sf` frame whose range matches ITS OWN fixed
 * `[0, driverLayers)` (never anything wider), and `useStageHost.ts` has
 * a symmetric guard (`claimedRangeRef`) that ignores a request outside
 * its OWN claim — see that file's `handleSessionOpen` doc comment.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { decodeStageControl, isStageControlFrame, } from '@unstable-legion/core';
import { acquireLeaderLock } from './useStagePipeline.js';
import { createLocalStageServeEngine, sameServedConfig, } from './localStageServeEngine.js';
import { DEFAULT_IDLE_EVICT_MS } from './stageSessionAdmission.js';
const DEFAULT_IDLE_SWEEP_MS = 30_000;
const DEFAULT_RESIDENT_POLL_MS = 1000;
const DEFAULT_SERVE_LOCK_NAME = 'unstable-legion-serve-first-stage-leader-v1';
export function useLocalStageServe(opts) {
    const { enabled, peer, residentStageZeroRef, desiredMaxSessions, idleSweepMs = DEFAULT_IDLE_SWEEP_MS, idleEvictMs = DEFAULT_IDLE_EVICT_MS, residentPollMs = DEFAULT_RESIDENT_POLL_MS, leaderLockName = DEFAULT_SERVE_LOCK_NAME, log = () => undefined, } = opts;
    const logRef = useRef(log);
    logRef.current = log;
    const priorityScoreRef = useRef(opts.priorityScore ?? (() => 0));
    priorityScoreRef.current = opts.priorityScore ?? (() => 0);
    const standingLedgerRef = useRef(opts.standingLedger);
    standingLedgerRef.current = opts.standingLedger;
    const [isLeader, setIsLeader] = useState(false);
    const lockRef = useRef(null);
    // Hold the DEDICATED serve lock — see this module's doc comment for why
    // it must not be the chat driver's own transient lock.
    useEffect(() => {
        if (!enabled) {
            setIsLeader(false);
            return;
        }
        let cancelled = false;
        void acquireLeaderLock(leaderLockName).then((lock) => {
            if (cancelled) {
                lock?.release();
                return;
            }
            if (lock) {
                lockRef.current = lock;
                setIsLeader(true);
            }
            else {
                setIsLeader(false);
            }
        });
        return () => {
            cancelled = true;
            lockRef.current?.release();
            lockRef.current = null;
            setIsLeader(false);
        };
    }, [enabled, leaderLockName]);
    const [sessions, setSessions] = useState([]);
    const [queueLength, setQueueLength] = useState(0);
    const [lastError, setLastError] = useState(undefined);
    const [loadedStageEntry, setLoadedStageEntry] = useState(undefined);
    const holderRef = useRef(null);
    const loadedStageEntryRef = useRef(undefined);
    const syncPublicState = useCallback(() => {
        const holder = holderRef.current;
        if (!holder) {
            setSessions((prev) => (prev.length === 0 ? prev : []));
            setQueueLength((prev) => (prev === 0 ? prev : 0));
            setLastError(undefined);
            loadedStageEntryRef.current = undefined;
            setLoadedStageEntry(undefined);
            return;
        }
        setSessions(holder.engine.getSessions());
        setQueueLength(holder.engine.getQueueLength());
        setLastError(holder.engine.getLastError());
        // Only push a NEW `loadedStageEntry` when its CONTENT changed — the
        // engine returns a fresh object each call, and a churning identity here
        // is what feeds `useStageHost`'s cap-publish effect a new array every
        // render. Content-compare (cheap, tiny object) so the entry's identity
        // stays stable across no-op syncs, keeping the cap publisher quiet.
        const entry = holder.engine.getLoadedStageEntry();
        if (JSON.stringify(entry) !== JSON.stringify(loadedStageEntryRef.current)) {
            loadedStageEntryRef.current = entry;
            setLoadedStageEntry(entry);
        }
    }, []);
    useEffect(() => {
        if (!enabled || !isLeader || !peer) {
            const holder = holderRef.current;
            if (holder) {
                holderRef.current = null;
                void holder.engine.stopAll('serving disabled').finally(syncPublicState);
            }
            else {
                syncPublicState();
            }
            return;
        }
        const meshPeer = peer;
        let cancelled = false;
        function adopt() {
            if (cancelled)
                return;
            const resident = residentStageZeroRef.current;
            const holder = holderRef.current;
            if (!resident) {
                if (holder) {
                    holderRef.current = null;
                    void holder.engine.stopAll('resident worker no longer available').finally(syncPublicState);
                }
                return;
            }
            const ceiling = desiredMaxSessions ?? resident.serveMaxSessions;
            const wantConfig = {
                modelId: resident.modelId,
                layerStart: resident.layerStart,
                layerEnd: resident.layerEnd,
                totalLayers: resident.totalLayers,
                ctxSize: resident.ctxSize,
                wireDtype: resident.wireDtype,
            };
            if (holder &&
                holder.clientIdentity === resident.client &&
                sameServedConfig(holder.config, wantConfig) &&
                holder.maxSessions === ceiling &&
                holder.epoch === resident.epoch) {
                return; // already correctly adopting this exact resident instance
            }
            if (holder) {
                holderRef.current = null;
                void holder.engine.stopAll('resident worker changed');
            }
            const engine = createLocalStageServeEngine({
                meshPeer,
                client: resident.client,
                config: wantConfig,
                maxSessions: ceiling,
                epoch: resident.epoch,
                priorityScore: (peerId) => priorityScoreRef.current(peerId),
                standingLedger: standingLedgerRef.current,
                log: (line) => logRef.current(line),
                onChange: syncPublicState,
            });
            holderRef.current = { engine, clientIdentity: resident.client, config: wantConfig, maxSessions: ceiling, epoch: resident.epoch };
            syncPublicState();
        }
        adopt();
        const pollTimer = setInterval(adopt, residentPollMs);
        const unsubTool = meshPeer.onTool((frame, peerId) => {
            const holder = holderRef.current;
            if (!holder || !isStageControlFrame(frame))
                return;
            const decoded = decodeStageControl(frame);
            if (!decoded)
                return;
            if (decoded.kind === 'stage.session.open') {
                // Only ours — see this module's "Cross-talk" doc section.
                if (decoded.payload.layerStart !== holder.config.layerStart || decoded.payload.layerEnd !== holder.config.layerEnd)
                    return;
                void holder.engine.handleSessionOpen(decoded.payload, peerId, decoded.callId);
            }
            else if (decoded.kind === 'stage.stop') {
                void holder.engine.handleStop(decoded.sessionId, peerId, decoded.payload.reason);
            }
        });
        const unsubFrame = meshPeer.onStageFrame((bytes, peerId) => {
            holderRef.current?.engine.handleStageFrame(bytes, peerId);
        });
        const idleTimer = setInterval(() => {
            holderRef.current?.engine.idleSweep(Date.now(), idleEvictMs);
        }, idleSweepMs);
        const unsubRoster = meshPeer.roster.subscribe((snapshot) => {
            holderRef.current?.engine.onRosterChange(new Set(snapshot.map((e) => e.peerId)));
        });
        const onPageHide = () => {
            void holderRef.current?.engine.stopAll('peer pagehide');
        };
        if (typeof window !== 'undefined')
            window.addEventListener('pagehide', onPageHide);
        return () => {
            cancelled = true;
            clearInterval(pollTimer);
            clearInterval(idleTimer);
            unsubTool();
            unsubFrame();
            unsubRoster();
            if (typeof window !== 'undefined')
                window.removeEventListener('pagehide', onPageHide);
            const holder = holderRef.current;
            holderRef.current = null;
            if (holder)
                void holder.engine.stopAll('serve effect torn down');
        };
    }, [enabled, isLeader, peer, residentStageZeroRef, desiredMaxSessions, idleSweepMs, idleEvictMs, residentPollMs, syncPublicState]);
    const getLoadedStageEntry = useCallback(() => loadedStageEntryRef.current, []);
    return {
        active: !!holderRef.current,
        sessions,
        queueLength,
        lastError,
        loadedStageEntry,
        getLoadedStageEntry,
    };
}
